/**
 * IB Space Vuex Store Module
 * Manages IB-specific state: active community, partner branding, UI
 */
import { getResponsiveLogo } from '@/utils/partnerLogos'

const ibModule = {
  namespaced: true,

  state: {
    // Active community context
    activeCommunity: null,       // The selected club object
    activeMembership: null,      // The user's membership { id, rol, club }
    userCommunities: [],         // All user's communities from API
    communityMemberCount: 0,     // Member count for active community
    communitySponsor: null,      // Main partner/sponsor for active community

    // Partner branding (from whitelabel API, clean names)
    partner: {
      id: null,
      name: '',
      logoUrl: '',
      responsiveLogoUrl: '',
      websiteUrl: '',
      primaryColor: '',
      // externalId of the partner's owned community. Used as the clubInviteCode
      // at registration so the end user is auto-joined to the IB community.
      // TODO(backend): populated from the whitelabel API once it exposes the
      // partner's main club externalId — see branding.service.js _extractPartnerData.
      clubExternalId: '',
      themes: { light: {}, dark: {} }
    },

    // Enabled features from whitelabel API
    features: [],

    // UI
    loading: false,
    brandingReady: false
  },

  mutations: {
    SET_ACTIVE_COMMUNITY(state, community) {
      state.activeCommunity = community
    },

    SET_ACTIVE_MEMBERSHIP(state, membership) {
      state.activeMembership = membership
    },

    SET_USER_COMMUNITIES(state, communities) {
      state.userCommunities = communities
    },

    SET_COMMUNITY_MEMBER_COUNT(state, count) {
      state.communityMemberCount = count
    },

    SET_COMMUNITY_SPONSOR(state, sponsor) {
      state.communitySponsor = sponsor
    },

    SET_PARTNER(state, partner) {
      state.partner = { ...state.partner, ...partner }
    },

    SET_LOADING(state, loading) {
      state.loading = loading
    },

    SET_FEATURES(state, features) {
      state.features = features
    },

    SET_BRANDING_READY(state) {
      state.brandingReady = true
    },

    CLEAR_COMMUNITY(state) {
      state.activeCommunity = null
      state.activeMembership = null
      state.communityMemberCount = 0
      state.communitySponsor = null
    },

    CLEAR_ALL(state) {
      state.activeCommunity = null
      state.activeMembership = null
      state.userCommunities = []
      state.communityMemberCount = 0
      state.communitySponsor = null
      // Preserve partner branding (logo, colors, features) across logout
      // so the login page still shows the partner logo
      state.loading = false
    }
  },

  actions: {
    async selectCommunity({ commit, dispatch }, { community, membership }) {
      commit('SET_ACTIVE_COMMUNITY', community)
      commit('SET_ACTIVE_MEMBERSHIP', membership)
      dispatch('fetchMemberCount', community.id)
      dispatch('fetchCommunitySponsor', community.id)
      dispatch('fetchClubPermissions', community.id)
    },

    /**
     * Club plan permissions (TOURNAMENT_CREATION, PUBLIC_CHANNELS, ...). The
     * reused trader views (ClubTournaments and friends) gate their actions on the
     * root `clubPermissions` list, which the trader app loads in its ClubIndex
     * wrapper. The IB app mounts those views directly, so it has to load the
     * list itself or every plan feature looks unavailable.
     */
    async fetchClubPermissions({ commit }, clubId) {
      try {
        const ClubRepository = require('@/repositories/club').default
        const response = await ClubRepository.getClubPermissions(clubId)
        if (response.status < 400 && Array.isArray(response.data)) {
          commit('setClubPermissions', response.data, { root: true })
        }
      } catch (error) {
        console.error('[IB Store] Failed to fetch club permissions:', error)
      }
    },

    async fetchCommunitySponsor({ commit }, clubId) {
      try {
        const ClubRepository = require('@/repositories/club').default
        const response = await ClubRepository.getClubPartners(clubId)
        if (response.status < 400 && response.data) {
          const data = response.data
          const mainPartner = data.partners
            ? data.partners.find(p => p.id === data.mainPartnerId) || data.partners[0]
            : null
          if (mainPartner) {
            commit('SET_COMMUNITY_SPONSOR', {
              id: mainPartner.id,
              name: mainPartner.shortName || mainPartner.fullName || '',
              logoUrl: getResponsiveLogo(mainPartner)
            })
          }
        }
      } catch (error) {
        console.error('[IB Store] Failed to fetch community sponsor:', error)
      }
    },

    async fetchMemberCount({ commit }, clubId) {
      try {
        const ClubRepository = require('@/repositories/club').default
        const response = await ClubRepository.getAllClubMembers(clubId)
        if (response.status < 400 && response.data) {
          const activeMembers = response.data.filter(m => m.rol !== 'PENDING')
          commit('SET_COMMUNITY_MEMBER_COUNT', activeMembers.length)
        }
      } catch (error) {
        console.error('[IB Store] Failed to fetch member count:', error)
      }
    },

    quitCommunity({ commit }) {
      commit('CLEAR_COMMUNITY')
      // Permissions belong to the community that was just left.
      commit('setClubPermissions', [], { root: true })
    },

    setUserCommunities({ commit }, communities) {
      commit('SET_USER_COMMUNITIES', communities)
    },

    setPartnerBranding({ commit }, partner) {
      commit('SET_PARTNER', partner)
    },

    setFeatures({ commit }, features) {
      commit('SET_FEATURES', features)
    },

    logout({ commit }) {
      commit('CLEAR_ALL')
      commit('setClubPermissions', [], { root: true })
    }
  },

  getters: {
    activeCommunity: (state) => state.activeCommunity,
    activeMembership: (state) => state.activeMembership,
    userCommunities: (state) => state.userCommunities,
    partner: (state) => state.partner,
    partnerName: (state) => state.partner.name || '',
    partnerLogoUrl: (state) => state.partner.logoUrl || state.partner.responsiveLogoUrl || '',
    // externalId of the partner's community, passed as clubInviteCode at register
    // to auto-join the new end user. Priority: the value the whitelabel API returns
    // (state.partner.clubExternalId) wins; until the backend exposes it, we fall back
    // to a build-time env var (VUE_APP_IB_COMMUNITY_EXTERNAL_ID) baked per-partner.
    // See TD-014. When the backend ships, the env var becomes a no-op automatically.
    partnerClubExternalId: (state) =>
      state.partner.clubExternalId || process.env.VUE_APP_IB_COMMUNITY_EXTERNAL_ID || '',
    isLoading: (state) => state.loading,
    brandingReady: (state) => state.brandingReady,
    enabledFeatures: (state) => state.features,
    hasFeature: (state) => (featureName) => state.features.includes(featureName),
    hasCommunity: (state) => state.activeCommunity !== null,
    memberCount: (state) => state.communityMemberCount,
    communitySponsor: (state) => state.communitySponsor,
    communityColor: (state) => {
      if (!state.activeCommunity) return '6366F1'
      return state.activeCommunity.primaryColor || '6366F1'
    },
    memberRole: (state) => state.activeMembership?.rol || '',
    isAdmin: (state) => ['ADMIN', 'FOUNDER'].includes(state.activeMembership?.rol),
    isTrader: (state) => state.activeMembership?.rol === 'MEMBER'
  }
}

export default ibModule
